import { renderNavbar } from '../components/navbar.js';
import { escapeHtml } from '../utils/html.js';
import { teamFlag } from '../utils/competitions.js';
import { findTeamProfile, listTeamProfiles, localizedProfileDataset } from '../data/team-profiles.js';
import { currentLocale, tr } from '../services/i18n.js';

export function renderTeams(container) {
  const teams = listTeamProfiles();
  const dataset = localizedProfileDataset();
  const selected = selectedTeamFromHash(teams);
  const shouldFocusProfile = hasSelectedTeamInHash();

  container.innerHTML = `
    <div class="page p-16 teams-page" style="padding-bottom:100px;">
      <div class="app-header">
        <div class="brand">
          <div class="brand-mark">FD</div>
          <div>
            <p class="brand-title">${tr('Album équipes', 'Teams album')}</p>
            <p class="brand-subtitle">${tr('Cartes coachs et fiches tactiques datées', 'Coach cards and dated tactical profiles')}</p>
          </div>
        </div>
        <a class="btn-secondary" href="#/competitions">${tr('Matchs', 'Matches')}</a>
      </div>

      <section class="teams-album-head">
        <p class="eyebrow">${escapeHtml(dataset.competition)}</p>
        <h1 class="heading-2" style="margin:0;">${tr('Cartes Panini tactiques', 'Tactical Panini cards')}</h1>
        <p class="text-muted" style="margin:8px 0 0;">${tr('Recto pour identifier la sélection, verso pour lire le coach, les systèmes et les principes attaque/milieu/défense.', 'Front to identify the team, back to read the coach, systems, and attack/midfield/defense principles.')}</p>
        <p class="text-muted" style="margin:10px 0 0;font-size:12px;">${tr('Dernière mise à jour', 'Last updated')}: ${escapeHtml(formatProfileDate(dataset.updatedAt))} · ${escapeHtml(dataset.sourceNote)}</p>
      </section>

      <div class="teams-layout">
        <section class="teams-card-grid" aria-label="${tr('Cartes équipes', 'Team cards')}">
          ${teams.map((team) => renderTeamSticker(team, selected.id)).join('')}
        </section>
        <section id="team-profile-panel" class="team-profile-panel" aria-live="polite" tabindex="-1">
          ${renderTeamProfile(selected)}
        </section>
      </div>
    </div>
  `;
  renderNavbar(container);

  if (shouldFocusProfile) {
    requestAnimationFrame(() => {
      const panel = container.querySelector('#team-profile-panel');
      if (!panel) return;
      panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
      panel.focus({ preventScroll: true });
    });
  }
}

function selectedTeamFromHash(teams) {
  const params = new URLSearchParams((window.location.hash.split('?')[1] || '').trim());
  const selected = params.get('team');
  return (selected && findTeamProfile(selected)) || teams[0];
}

function hasSelectedTeamInHash() {
  const params = new URLSearchParams((window.location.hash.split('?')[1] || '').trim());
  return Boolean(params.get('team'));
}

function renderTeamSticker(team, selectedId) {
  const flag = teamFlag(team.name, team.code);
  const active = team.id === selectedId ? ' active' : '';
  const href = `#/teams?team=${encodeURIComponent(team.id)}`;
  return `
    <a class="panini-card${active}" href="${href}" aria-label="${tr('Voir la fiche', 'View profile')} ${escapeHtml(team.name)}">
      <span class="panini-card-shine" aria-hidden="true"></span>
      <span class="panini-card-top">
        <span class="panini-card-code">${escapeHtml(team.code)}</span>
        <span class="panini-card-status">${escapeHtml(team.status)}</span>
      </span>
      <span class="panini-portrait">
        ${renderTeamFlag(team, flag)}
      </span>
      <span class="panini-card-name">${escapeHtml(team.name)}</span>
      <span class="panini-card-coach">${escapeHtml(team.coach.name)}</span>
      <span class="panini-card-line">${escapeHtml(team.paniniLine)}</span>
      <span class="panini-card-tags">
        ${team.tags.slice(0, 3).map((tag) => `<span>${escapeHtml(tag)}</span>`).join('')}
      </span>
    </a>
  `;
}

function renderTeamFlag(team, flag) {
  const fallback = `<span class="panini-flag-fallback">${escapeHtml(team.code)}</span>`;
  if (flag?.src) {
    const srcset = flag.srcset ? ` srcset="${escapeHtml(flag.srcset)}"` : '';
    return `<img class="panini-flag-image" src="${escapeHtml(flag.src)}"${srcset} width="72" height="54" loading="lazy" alt="${escapeHtml(flag.alt)}">`;
  }
  if (flag?.emoji) {
    return `<span class="panini-flag-emoji" role="img" aria-label="${escapeHtml(flag.alt)}">${escapeHtml(flag.emoji)}</span>`;
  }
  return fallback;
}

function renderTeamProfile(team) {
  const systems = team.preferredSystems.map((system) => `<span class="team-system-pill">${escapeHtml(system)}</span>`).join('');
  return `
    <article class="team-profile-card">
      <div class="team-profile-head">
        <div>
          <p class="eyebrow">${tr('Verso de carte', 'Card back')}</p>
          <h2 class="heading-2" style="margin:0;">${escapeHtml(team.name)}</h2>
          <p class="text-muted" style="margin:6px 0 0;">${escapeHtml(team.group)} · ${tr('sélectionneur depuis', 'coach since')} ${escapeHtml(team.coach.roleSince)}</p>
        </div>
        <div class="team-profile-badge">${escapeHtml(team.code)}</div>
      </div>

      <div class="coach-strip">
        <div>
          <p class="coach-label">${tr('Entraîneur', 'Coach')}</p>
          <p class="coach-name">${escapeHtml(team.coach.name)}</p>
          <p class="text-muted" style="margin:6px 0 0;">${escapeHtml(team.coach.formation)}</p>
        </div>
        <p class="coach-record">${escapeHtml(team.coach.record)}</p>
      </div>

      <div class="team-system-row">${systems}</div>

      <div class="tactical-scout-grid">
        ${renderTacticalBlock(tr('Attaque', 'Attack'), team.attack)}
        ${renderTacticalBlock(tr('Milieu', 'Midfield'), team.midfield)}
        ${renderTacticalBlock(tr('Défense', 'Defense'), team.defense)}
        ${renderTacticalBlock('Transitions', team.transitions)}
      </div>

      <div class="team-profile-lists">
        ${renderList(tr('Points forts', 'Strengths'), team.strengths)}
        ${renderList(tr('Points de vigilance', 'Watch points'), team.risks)}
      </div>

      <p class="text-muted" style="font-size:12px;margin:16px 0 0;">${tr('Source profil', 'Profile source')}: ${escapeHtml(team.coach.source)} · ${escapeHtml(localizedProfileDataset().sourceNote)}</p>
    </article>
  `;
}

function renderTacticalBlock(title, body) {
  return `
    <section class="tactical-scout-block">
      <h3>${escapeHtml(title)}</h3>
      <p>${escapeHtml(body)}</p>
    </section>
  `;
}

function renderList(title, items) {
  return `
    <section>
      <h3>${escapeHtml(title)}</h3>
      <ul>
        ${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
      </ul>
    </section>
  `;
}

function formatProfileDate(value) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(currentLocale(), { day: '2-digit', month: 'long', year: 'numeric' });
}
